import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { LinearProgress, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import AntennaServiceApi from '../antenna/service'; // Import the new AntennaServiceApi
import RinexServiceApi from './service';

const rinexservice = new RinexServiceApi();
const antennaservice = new AntennaServiceApi(); // Instantiate the new service

export default function RINEXServiceForm() {
  let navigate = useNavigate();

  let { id } = useParams();

  const [loading, setLoading] = useState(true); // Set initial loading to true
  const [errors, setErrors] = useState({});

  const [device, setDevice] = useState("/dev/ttySC1");
  const [deviceValid, setDeviceValid] = useState(true);

  const [baudrate, setBaudrate] = useState("460800");

  // RINEX Header Values
  const [markerName, setMarkerName] = useState("AAAA");
  const [markerNameValid, setMarkerNameValid] = useState(true);

  const [markerNumber, setMarkerNumber] = useState(1);
  const [markerNumberValid, setMarkerNumberValid] = useState(true);

  const [observer, setObserver] = useState("");
  const [observerValid, setObserverValid] = useState(true);

  const [agency, setAgency] = useState("");
  const [agencyValid, setAgencyValid] = useState(true);

  // New state for antennas
  const [antennas, setAntennas] = useState([]);
  const [selectedAntenna, setSelectedAntenna] = useState(null); // Stores the selected antenna object
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option) => option.type + ' ' + option.radome,
  });

  // GNSS Recevier Config
  const [RTKGPS, setRTKGPS] = useState(true);
  const [RTKGLO, setRTKGLO] = useState(false);
  const [RTKGAL, setRTKGAL] = useState(false);
  const [RTKBDS, setRTKBDS] = useState(false);

  const [interval, setInterval] = useState(30);

  useEffect(() => {
    console.log("Service ID: " + id);
    fetchAntennas(); // Fetch antennas first
  }, [id]);

  const fetchAntennas = () => {
    antennaservice.getAll().then((response) => {
      setAntennas(response); // Assuming results is an array of antenna objects
      populateForm(response); // Then populate the form
    }).catch((e) => {
      console.error("Failed to fetch antennas:", e);
      setLoading(false);
    });
  };

  const populateForm = (antennas) => {
    if(id && id !== 'new') { // Populate form data if editing existing RTK service
      rinexservice.get(id).then((response) => {
        // Serial Settings
        setDevice(response.path);
        setBaudrate(response.baudrate);

        // GNSS Settings
        setRTKGPS(response.gps);
        setRTKGLO(response.glonass);
        setRTKGAL(response.galileo);
        setRTKBDS(response.beidou);
        setInterval(response.interval)

        // Header settings
        setMarkerName(response.header_marker_name);
        setMarkerNumber(response.header_marker_number);
        setObserver(response.header_observer);
        setAgency(response.header_agency);
        // Set selected antenna
        if (response.antenna && antennas.length > 0) {
          const antennaObject = antennas.find(ant => ant.id === response.antenna);
          if (antennaObject) {
            setSelectedAntenna(antennaObject);
          } else {
            console.warn(`Antenna with ID ${response.antenna} not found in antenna list`);
            setSelectedAntenna(null);
          }
        }
        setLoading(false);
      }).catch((e) => {
        console.error("Failed to fetch RinexRecorder:", e);
        setLoading(false);
      });
    }
    else {
      if (antennas && antennas.length > 0) {
        const hema45Antenna = antennas.find(ant => ant.type === 'HEMA45');
        if (hema45Antenna) {
          setSelectedAntenna(hema45Antenna);
        } else {
          setSelectedAntenna(antennas[0]);
        }
      }
      setLoading(false);
    }
  };

  const handleDeviceChange = (event) => {
    let newDevice = cleanDevice(event.target.value);
    setDeviceValid(validateSerialPort(newDevice));
    setDevice(newDevice);
  };

  const handleMarkerNameChange = (event) => {
    let newMarkerName = cleanMarkerName(event.target.value);
    setMarkerName(newMarkerName);
    setMarkerNameValid(validateMarkerName(newMarkerName));
  };

  const handleMarkerNumberChange = (event) => {
    setMarkerNumber(event.target.value);
    setMarkerNumberValid(validateMarkerNumber(event.target.value));
  };

  const handleObserverChange = (event) => {
    setObserver(event.target.value);
    setObserverValid(validateObserver(event.target.value));
  };

  const handleAgencyChange = (event) => {
    setAgency(event.target.value);
    setAgencyValid(validateAgency(event.target.value));
  };

  // Removed handleAntennaNumberChange and handleAntennaTypeChange

  function resetCasterValidation() {
    // This function might be obsolete or need re-evaluation
  }

  const SubmitButton = () => {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const submit = () => {
      setSubmitting(true);

      const data = {
        connection_type: "SERIAL",
        path: device,
        baudrate: baudrate,
        header_marker_name: markerName,
        header_marker_number: markerNumber,
        header_observer: observer,
        header_agency: agency,
        antenna: selectedAntenna ? selectedAntenna.id : null, // Send the selected antenna ID
        gps: RTKGPS,
        glonass: RTKGLO,
        galileo: RTKGAL,
        beidou: RTKBDS,
        interval: interval
      };

      if (id && id !== 'new') {
        rinexservice.update(id, data).then((response) => {
            rinexservice.sync(id);
            setSubmitted(true);
            setSubmitting(false);
            navigate("/gnss/rinex/");
          }).catch((e) => {
            setSubmitting(false);
            setSubmitted(false);
            setErrors(e.response.data);
          });
      } else {
        rinexservice.create(data).then((response) => {
            setSubmitted(true);
            setSubmitting(false);
            navigate(`/gnss/rinex/${response.id}`);
          }).catch((e) => {
            setSubmitting(false);
            setSubmitted(false);
            setErrors(e.response.data);
          });
      }
    };
    return (
      <React.Fragment>
        <Button variant="contained" onClick={submit} disabled={(submitting || submitted)}>
          {!submitted && !submitting && "Save"}
          {submitting && (<CircularProgress size="1rem" color="inherit" />)}
          {!submitting && submitted && <CheckIcon color="success" />}
          {!submitting && submitted && <ErrorOutlineIcon color="error" />}
        </Button>
      </React.Fragment>
    );
  }

  const DeleteButton = () => {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const deleteservice = () => {
      setSubmitting(true);
      rinexservice.delete(id).then((response) => {
          setSubmitted(true);
          setSubmitting(false);
          navigate("/gnss/rinex/"); // Navigate after deletion
        });
    };
    return (
      <React.Fragment>
        <Button variant="contained" color="error" onClick={deleteservice} disabled={(submitting || submitted)}>
          {!submitted && !submitting && "Remove Service"}
          {submitting && (<CircularProgress size="1rem" color="inherit" />)}
          {!submitting && submitted && <CheckIcon color="success" />}
          {!submitting && submitted && <ErrorOutlineIcon color="error" />}
        </Button>
      </React.Fragment>
    );
  }

  if (loading) {
    return <LinearProgress />
  }
  else {
    return (
      <React.Fragment>
        <h1 className="page-title"><span className="fw-semi-bold">GNSS Connector</span></h1>
        <Stack>
          <Paper style={{ marginTop: "2em", marginBottom: "2em", padding: "1em", minWidth: "15vw" }} >
            <h3>{id ? "RINEX Logger" : "New RINEX Logger"}</h3>
            <Stack spacing={2}>
              <React.Fragment>
                <Typography>Log RINEX data continuously from the GNSS Receiver.</Typography>
                <h4>Serial Settings</h4>
                <TextField
                   id="device"
                   label="Device port"
                   onChange={handleDeviceChange}
                   value={device}
                   error={!deviceValid}
                   helperText={!deviceValid ? "Invalid device port" : "Enter a valid device port"}
                 />
                  {errors.hasOwnProperty("path") && (errors.path.map((error) => {
                      return <Alert severity="error">{error}</Alert>
                  }))}
               <Stack spacing={2}>
                 <FormControl>
                   <InputLabel id="baudrate">Baudrate</InputLabel>
                   <Select
                     labelId="baudrate"
                     id="baudrate-select"
                     value={baudrate}
                     label="baudrate"
                     onChange={(e) => setBaudrate(e.target.value)}
                     style={{ minWidth: "15em" }}
                   >
                       <MenuItem value={460800}>460800</MenuItem>
                       <MenuItem value={230400}>230400</MenuItem>
                       <MenuItem value={115200}>115200</MenuItem>
                       <MenuItem value={57600}>57600</MenuItem>
                       <MenuItem value={38400}>38400</MenuItem>
                       <MenuItem value={19200}>19200</MenuItem>
                       <MenuItem value={9600}>9600</MenuItem>
                       <MenuItem value={4800}>4800</MenuItem>
                   </Select>
                 </FormControl>
               </Stack>

                <h4>GNSS Settings</h4>
                <FormControl>
                  <FormLabel>Select Constellations</FormLabel>
                  <FormGroup row>
                    <FormControlLabel control={<Checkbox onClick={() => setRTKGPS(!RTKGPS)} checked={RTKGPS} />
                    } label="GPS" />
                    <FormControlLabel control={<Checkbox onClick={() => setRTKGLO(!RTKGLO)} checked={RTKGLO} />
                    } label="GLONASS" />
                    <FormControlLabel control={<Checkbox onClick={() => setRTKGAL(!RTKGAL)} checked={RTKGAL} />
                    } label="GALILEO" />
                    <FormControlLabel control={<Checkbox onClick={() => setRTKBDS(!RTKBDS)} checked={RTKBDS} />
                    } label="BeiDou" />
                  </FormGroup>
                </FormControl>
                <Stack spacing={2}>
                 <FormControl>
                   <InputLabel id="interval">Interval</InputLabel>
                   <Select
                     labelId="interval"
                     id="interval-select"
                     value={interval}
                     label="interval"
                     onChange={(e) => setInterval(e.target.value)}
                     style={{ minWidth: "15em" }}
                   >
                       <MenuItem value={1}>1 second</MenuItem>
                       <MenuItem value={5}>5 seconds</MenuItem>
                       <MenuItem value={10}>10 seconds</MenuItem>
                       <MenuItem value={30}>30 seconds</MenuItem>
                       <MenuItem value={60}>60 seconds</MenuItem>
                   </Select>
                 </FormControl>
               </Stack>
               <h4>RINEX Header</h4>
                <TextField
                  id="header-marker-name"
                  label="Marker Name"
                  onChange={handleMarkerNameChange}
                  value={markerName}
                  error={!markerNameValid}
                  helperText={!markerNameValid ? "Must be 4 characters." : "Enter a 4 character name (e.g. AAAA)."}
                />
                <TextField
                  id="header-marker-number"
                  label="Marker Number"
                  onChange={handleMarkerNumberChange}
                  value={markerNumber}
                  error={!markerNumberValid}
                  helperText={!markerNumberValid ? "Enter a reasonable integer..." : "Enter a marker number"}
                />
                <TextField
                  id="header-observer"
                  label="Observer"
                  onChange={handleObserverChange}
                  value={observer}
                  error={!observerValid}
                  helperText={!observerValid ? "No more than 20 characters allowed." : "Enter an observer name"}
                />
                <TextField
                  id="header-agency"
                  label="Agency"
                  onChange={handleAgencyChange}
                  value={agency}
                  error={!agencyValid}
                  helperText={!agencyValid ? "No more than 40 characters allowed." : "Enter an agency name"}
                />
                {/* Replaced Antenna Number and Antenna Type TextFields with a Select for Antenna */}
                <Autocomplete
                  fullWidth
                  options={antennas}
                  getOptionLabel={(option) => option.type ? `${option.type} (${option.radome})` : ""}
                  filterOptions={filterOptions}
                  value={selectedAntenna}
                  onChange={(event, newValue) => {
                    setSelectedAntenna(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} label="Antenna" />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </React.Fragment>
              <SubmitButton />
              {id && id !== 'new' && <DeleteButton />}
              {id && 
                <Typography style={{color: "grey"}}>Service ID: {id}</Typography>
              }
            </Stack>
          </Paper>
        </Stack >
      </React.Fragment>
    );
  }
}


const cleanMarkerName = (value) => {
  return value.toUpperCase();
}

const validateMarkerName = (value) => {
  if (/^[A-Z0-9][A-Z0-9][A-Z0-9][A-Z0-9]$/.test(value)) {
    return true;
  }
  return false;
}

const validateMarkerNumber = (value) => {
  if (/^[0-9]*$/.test(value)) {
    if (value > 0 && value <= 2147483647) {
      return true;
    }
  }
  return false;
}

const validateObserver = (value) => {
  if (value.length <= 20) {
    return true;
  }
  return false;
}

const validateAgency = (value) => {
  if (value.length <= 40) {
    return true;
  }
  return false;
}

// Removed cleanAntennaNumber, validateAntennaNumber, cleanAntennaType, validateAntennaType

const validatePort = (port) => {
  if (/^[0-9][0-9]?[0-9]?[0-9]?[0-9]?$/.test(port)) {
    if (port > 0 && port <= 65535) {
      return true;
    }
  }
  return false;
}

const cleanInterval = (port) => {
  return port.replace(/[^0-9.]/g, '');
}

const cleanInteger = (port) => {
  return port.replace(/[^0-9.]/g, '');
}

const cleanDevice = (value) => {
  return value.replace(" ", "");
}

const validateSerialPort = (value) => {
  if (/[&\\#,+()$~%.'":*?<>{}]/.test(value)) {
    return false;
  }
  else if (value.length > 100) {
    return false;
  }
  else {
    return true;
  }
}

const validateRadioFreq = (value) => {
  if (/^[0-9]?[0-9]?[0-9]\.[0-9][0-9]?[0-9]?[0-9]?[0-9]?$/.test(value)) {
    return true;
  }
  return false;
}

const validateBroadcastId = (value) => {
  if (/^[0-9][0-9]?[0-9]?[0-9]?$/.test(value)) {
    if (value >= 0 && value <= 4096) {
      return true;
    }
  }
  return false;
}
